import { createFileRoute, stripSearchParams, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { motion } from "framer-motion";
import { Search, Clock, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/Eyebrow";
import { SafeImage } from "@/components/SafeImage";
import {
  RECIPE_CATEGORY_LABELS,
  formatRecipeDate,
  type RecipeCategory,
} from "@/data/recipes";
import { listPublicRecipes } from "@/lib/public.functions";
import { cn } from "@/lib/utils";
import { useRecipesHidden } from "@/hooks/useSiteSettings";
import { assertSectionVisible } from "@/lib/visibility";

const CATS: RecipeCategory[] = ["grigliate", "primi", "secondi", "festivita", "salumi"];

const searchSchema = z.object({
  cat: fallback(z.string(), "").default(""),
  q: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/ricette/")({
  beforeLoad: () => assertSectionVisible("hide_recipes"),
  validateSearch: zodValidator(searchSchema),
  // I valori predefiniti dei filtri restano fuori dall'URL: /ricette non deve redirigere a /ricette?cat=…
  search: { middlewares: [stripSearchParams({ cat: "", q: "" })] },
  head: () => ({
    meta: [
      { title: "Ricette — La Bottega delle Carni da Marco" },
      {
        name: "description",
        content:
          "Ricette di Marco e della bottega: classici della tradizione molisana, idee per la grigliata e consigli del macellaio per cuocere bene la carne.",
      },
      { property: "og:title", content: "Ricette dalla bottega" },
      {
        property: "og:description",
        content: "Idee, consigli e ricette dal nostro laboratorio.",
      },
      { property: "og:url", content: "https://labottegadellecarnidamarco.it/ricette" },
    ],
    links: [{ rel: "canonical", href: "https://labottegadellecarnidamarco.it/ricette" }],
  }),
  component: RecipesIndex,
});

function RecipesIndex() {
  const recipesHidden = useRecipesHidden();
  const navigate = useNavigate({ from: "/ricette/" });
  const search = Route.useSearch();
  useEffect(() => {
    if (recipesHidden) navigate({ to: "/", replace: true });
  }, [recipesHidden, navigate]);
  if (recipesHidden) return null;
  const { data } = useQuery({
    queryKey: ["public-recipes"],
    queryFn: () => listPublicRecipes(),
  });
  const RECIPES = data?.recipes ?? [];

  const filtered = useMemo(() => {
    let list = [...RECIPES];
    if (search.cat) list = list.filter((r) => r.category === search.cat);
    if (search.q) {
      const q = search.q.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.excerpt.toLowerCase().includes(q) ||
          r.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return list.sort((a, b) => b.date.localeCompare(a.date));
  }, [search, RECIPES]);

  const hasFilters = search.cat || search.q;

  return (
    <>
      <section className="bg-brand-cream pt-16 pb-12 border-b border-brand-gold/20">
        <div className="container mx-auto px-4 text-center">
          <Eyebrow>Dal laboratorio</Eyebrow>
          <h1 className="font-display text-brand-charcoal text-4xl md:text-6xl mt-3">
            Ricette dalla bottega
          </h1>
          <p className="text-brand-warm-gray mt-4 max-w-2xl mx-auto font-italic-serif italic text-lg">
            Idee, consigli e ricette di Marco per portare in tavola la nostra carne
            come si deve.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-md p-4 md:p-6 shadow-[var(--shadow-soft)] mb-10">
            <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
              <div className="flex-1 relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-warm-gray"
                />
                <input
                  type="search"
                  placeholder="Cerca una ricetta..."
                  value={search.q}
                  onChange={(e) =>
                    navigate({ to: ".", search: (p: any) => ({ ...p, q: e.target.value }) })
                  }
                  className="w-full pl-9 pr-3 py-2.5 border border-brand-charcoal/15 rounded-md bg-brand-cream/40 text-sm focus:outline-none focus:border-brand-red"
                />
              </div>
              {hasFilters && (
                <button
                  onClick={() => navigate({ to: ".", search: { cat: "", q: "" } })}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-brand-red hover:underline"
                >
                  <X size={14} /> Reset
                </button>
              )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => navigate({ to: ".", search: (p: any) => ({ ...p, cat: "" }) })}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors",
                  !search.cat
                    ? "bg-brand-red border-brand-red text-white"
                    : "border-brand-charcoal/20 hover:border-brand-red text-brand-charcoal"
                )}
              >
                Tutte
              </button>
              {CATS.map((c) => {
                const on = search.cat === c;
                return (
                  <button
                    key={c}
                    onClick={() => navigate({ to: ".", search: (p: any) => ({ ...p, cat: c }) })}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-xs uppercase tracking-wider border transition-colors",
                      on
                        ? "bg-brand-red border-brand-red text-white"
                        : "border-brand-charcoal/20 hover:border-brand-red text-brand-charcoal"
                    )}
                  >
                    {RECIPE_CATEGORY_LABELS[c]}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="text-sm text-brand-warm-gray mb-4">
            {filtered.length} ricett{filtered.length === 1 ? "a" : "e"}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((r, i) => (
              <Reveal key={r.slug} delay={Math.min(i * 0.05, 0.3)}>
                <Link
                  to="/ricette/$slug"
                  params={{ slug: r.slug }}
                  className="group block bg-white rounded-md overflow-hidden lift-on-hover zoom-img-parent shadow-[var(--shadow-soft)] h-full"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-brand-cream-dark">
                    <SafeImage
                      src={r.cover}
                      alt={r.title}
                      className="zoom-img w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] text-brand-warm-gray">
                      <span className="text-brand-red">
                        {RECIPE_CATEGORY_LABELS[r.category]}
                      </span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock size={11} /> {r.readingTime} min
                      </span>
                    </div>
                    <h2 className="font-display text-xl md:text-2xl text-brand-charcoal mt-3 leading-tight group-hover:text-brand-red transition-colors">
                      {r.title}
                    </h2>
                    <p className="text-sm text-brand-warm-gray mt-3 leading-relaxed line-clamp-3">
                      {r.excerpt}
                    </p>
                    <div className="mt-4 text-[11px] uppercase tracking-[0.16em] text-brand-warm-gray">
                      {formatRecipeDate(r.date)}
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20 text-brand-warm-gray"
            >
              Nessuna ricetta trovata. Prova a modificare i filtri.
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}

function RecipesHiddenNotice() {
  return (
    <section className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md text-center py-16">
        <p className="text-xs uppercase tracking-[0.3em] text-brand-warm-gray">Sezione</p>
        <h1 className="font-display text-3xl md:text-4xl text-brand-charcoal mt-3">
          Ricette non disponibili
        </h1>
        <p className="mt-3 text-sm text-brand-warm-gray">
          Le ricette non sono attualmente pubblicate. Torna presto a trovarci.
        </p>
        <Link to="/" className="mt-6 inline-flex items-center rounded-md bg-brand-red px-6 py-3 text-xs uppercase tracking-[0.18em] text-white hover:bg-brand-red-hover">
          Torna alla home
        </Link>
      </div>
    </section>
  );
}